import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { noop } from '@belong/common';
import {
  fetchUserVerifications,
  fetchUserIdentityVerification,
  createUserIdentityVerification,
} from 'store/redux/user/actions';
import {
  selectUserIdentityVerification,
  selectUserIsLoadingIdentityVerification,
  selectUserIsLoadingVerifications,
  selectUserVerifications,
} from 'store/redux/user/selectors';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

export function useUserIdentityVerification(
  userId?: number,
  shouldCreateVerificationIfNecessary?: boolean,
) {
  const {
    isLoadingVerifications,
    verification,
    identityVerification,
    isLoadingIdentityVerification,
  } = useSelector((state) => ({
    isLoadingVerifications: selectUserIsLoadingVerifications(state),
    verification: selectUserVerifications(state).find(
      (v: { type?: string }) => v.type === 'Identity',
    ),
    identityVerification: selectUserIdentityVerification(state),
    isLoadingIdentityVerification:
      selectUserIsLoadingIdentityVerification(state),
  }));
  const [hasFetchedIdentityVerification, setHasFetchedIdentityVerification] =
    useState(!!identityVerification);

  // `useAppDispatch` rather than `useDispatch`: `clientMiddleware` resolves a
  // `promise` action to the client's result, which is what the reads below are of.
  const dispatch = useAppDispatch();
  const fetchIdentityVerification = useCallback(
    async (_userId: number) => {
      try {
        const verifications = await dispatch(fetchUserVerifications(_userId));
        const identityVerificationId = verifications?.find(
          (v: { type?: string }) => v.type === 'Identity',
        )?.id;
        let _identityVerification = null;
        if (identityVerificationId) {
          _identityVerification = await dispatch(
            fetchUserIdentityVerification(identityVerificationId),
          );
        }
        if (
          (!_identityVerification ||
            (_identityVerification?.status !== 'Passed' &&
              !_identityVerification.sessionToken)) &&
          shouldCreateVerificationIfNecessary
        ) {
          _identityVerification = await dispatch(
            createUserIdentityVerification(_userId),
          );
        }
      } catch (_err) {
        // TODO: Handle Error
      } finally {
        setHasFetchedIdentityVerification(true);
      }
    },
    [dispatch, shouldCreateVerificationIfNecessary],
  );

  useEffect(() => {
    if (!!userId && !hasFetchedIdentityVerification) {
      fetchIdentityVerification(userId);
    }
  }, [userId, hasFetchedIdentityVerification, fetchIdentityVerification]);

  return {
    hasFetchedIdentityVerification,
    isLoadingIdentityVerification:
      isLoadingVerifications || isLoadingIdentityVerification,
    identityVerification,
    verification,
    refetchIdentityVerification: () =>
      userId ? fetchIdentityVerification(userId) : noop,
  };
}
